import type { WhatsappSelectorsType } from "@whatslly/types/integrations/im/WhatsappGetSelectorsResponse";
import type { IDOMInjectorService, SelectiveEntireDaySyncStore, SelectiveSyncStore } from "../../commons/IDOMInjectorService";
import { FRAUD_DOM_CLASSNAMES, STATUSBAR_NAMESPACE, TEMPLATES_NAMESPACE } from "../../commons/IDOMInjectorService";
import { DOMInjector } from "./DOMInjector";
import { StatusBarWidgetApp } from "../status-bar/StatusBarWidgetApp";
import { TemplatesWidget } from "../templates-widget/TemplatesWidget";
import type { SuccessfullQueryResponse } from "@whatslly/types/QueryResponse";
import { inject, injectable } from "inversify";
import type { InstantMessagingService } from "@whatslly/types/instant-messaging/InstantMessagingService";
import { IInstantMessagingServiceSymbol } from "@whatslly/types/instant-messaging/InstantMessagingService";
import { setTheme } from "@whatslly/ui-components/styles/useTheme";
import type { ListenerService } from "@whatslly/types/instant-messaging/ListenerService";
import { DOM_EVENT_TYPES, IListenerServiceSymbol } from "@whatslly/types/instant-messaging/ListenerService";
import { widgetClient } from "@whatslly/commons/widgetClient";
import { WidgetCommands } from "@whatslly/commons/widgetCommands";
import type { SerializedMessage } from "@whatslly/types/instant-messaging/messagesEmitter";
import { formatBytes } from "@whatslly/commons/utils";
import { MessageSynced, MessageSyncInvalid } from "../messageSynced/messageSynced";
import type { Translations } from "../../i18nService";
import { EntireDayCheckbox, MessageCheckbox } from "../messageCheckbox/messageCheckbox";
import sortBy from "lodash/sortBy";
import type { IMSelectorsUnion } from "@whatslly/types/integrations/im/IMSelectorsTypes";
const CLASS_NAMES = {
  SYNCED_THREAD: "synced-message",
  ENTIRE_DAY: "whatslly-entireDayContainer",
  MESSAGE_CONTAINER: "whatslly-messageContainer",
  SELECTED_MESSAGE: "whatslly-selectedMessage",
  ...FRAUD_DOM_CLASSNAMES,
} as const;
const CHAT_CHECKBOXES_NAMESPACE = "chat-checkboxes";
const CHAT_SYNCED_NAMESPACE = CLASS_NAMES.SYNCED_THREAD;

@injectable()
export class DOMInjectorService implements IDOMInjectorService {
  constructor(
    @inject(IInstantMessagingServiceSymbol) private readonly instantMessagingService: InstantMessagingService,
    @inject(IListenerServiceSymbol) private readonly domListenerService: ListenerService
  ) {}

  injectStatusBarWidgetAndTemplates(queryResponse: SuccessfullQueryResponse, selectors: WhatsappSelectorsType | undefined) {
    DOMInjector.detachByNamespace([STATUSBAR_NAMESPACE, TEMPLATES_NAMESPACE]);
    const wapApp = selectors && document.querySelector<HTMLDivElement>(selectors.css.app);
    const props = { widgetOpen: !wapApp || !wapApp.classList.contains("whatslly-hidden-widget-open"), queryResponse };
    const { headerElement: header, footerElement: footer } = this.instantMessagingService;
    if (queryResponse && !Array.isArray(queryResponse.person) && queryResponse.person) {
      header && DOMInjector.injectToDOM({ host: header, Component: StatusBarWidgetApp, namespace: STATUSBAR_NAMESPACE, props });
      footer && DOMInjector.injectToDOM({ host: footer, Component: TemplatesWidget, namespace: TEMPLATES_NAMESPACE, props });
    }
    if (queryResponse && queryResponse.isGroup) {
      footer && DOMInjector.injectToDOM({ host: footer, Component: TemplatesWidget, namespace: TEMPLATES_NAMESPACE, props });
    }
  }

  startListenToThemeModeChanged() {
    const darkMode = document.body.classList.contains("dark");
    setTheme({ darkMode });
    this.domListenerService.on(DOM_EVENT_TYPES.THEME_CHANGED, ({ darkMode }) => {
      setTheme({ darkMode });
      widgetClient.send(WidgetCommands.THEME_MODE_CHANGED, { darkMode });
    });
  }

  private getMessageElementSelector(messageId: string, selectors: IMSelectorsUnion): string {
    const lastMessageIdPart = messageId?.includes("_") ? messageId.split("_")[messageId.split("_").length - 1] : undefined;
    //TODO remove oldSelector after all the clients use new WhatsApp web latest version
    const oldSelector = `[${selectors.domListener.threadElementSelector}*="${messageId}"]`;
    const lastMessageIdPartSelector = lastMessageIdPart ? `[${selectors.domListener.threadElementSelector}*="${lastMessageIdPart}"]` : undefined;
    return [lastMessageIdPartSelector, oldSelector].filter(Boolean).join(", ");
  }

  private getMessageElementsToMask(messageElement: HTMLElement, selectors: IMSelectorsUnion): HTMLElement[] {
    const targets: HTMLElement[] = [];

    if (selectors.css.anchorTopLevelHref) {
      const anchorSelector = selectors.css.anchorTopLevelHref.replaceAll("&", ":scope");
      const anchor = messageElement.querySelector<HTMLElement>(anchorSelector);
      if (anchor) targets.push(anchor);
    }

    if (selectors.css.messageBody) {
      const messageBodySelector = selectors.css.messageBody.replaceAll("&", ":scope");
      messageElement.querySelectorAll<HTMLElement>(messageBodySelector).forEach((node) => {
        targets.push(node);
      });
    }

    return Array.from(new Set(targets));
  }

  private applyFraudClassToMessageElement(messageElement: HTMLElement, selectors: IMSelectorsUnion) {
    const elements = this.getMessageElementsToMask(messageElement, selectors);
    for (const el of elements) {
      el.classList.add(CLASS_NAMES.FRAUD_MESSAGE);
    }
  }

  markAsSynced(messageId: string, selectors: WhatsappSelectorsType | undefined): string | undefined {
    if (!selectors) {
      return;
    }
    const messageElement = document.querySelector<HTMLDivElement>(this.getMessageElementSelector(messageId, selectors));
    const element = messageElement?.querySelector<HTMLDivElement>(selectors.css.messageContainer);
    if (!element || !messageElement || messageElement.classList.contains(CLASS_NAMES.SYNCED_THREAD)) {
      return messageId;
    }
    messageElement.classList.add(CLASS_NAMES.SYNCED_THREAD);
    DOMInjector.injectToDOM({ host: element, Component: MessageSynced, namespace: CHAT_SYNCED_NAMESPACE });
  }

  hideFraudMessage(messageId: string, selectors: IMSelectorsUnion | undefined): string | undefined {
    if (!selectors) return;
    const messageElement = document.querySelector<HTMLDivElement>(this.getMessageElementSelector(messageId, selectors));
    if (!messageElement) return;

    this.applyFraudClassToMessageElement(messageElement, selectors);
    return messageId;
  }

  revealFraudMessage(messageId: string, selectors: IMSelectorsUnion | undefined): string | undefined {
    if (!selectors) return;
    const messageElement = document.querySelector<HTMLDivElement>(this.getMessageElementSelector(messageId, selectors));
    if (!messageElement) return;

    const elements = this.getMessageElementsToMask(messageElement, selectors);
    for (const el of elements) {
      el.classList.remove(CLASS_NAMES.FRAUD_MESSAGE);
      el.classList.add(CLASS_NAMES.FRAUD_MESSAGE_SHOW);
    }
    return messageId;
  }

  clearFraudMessageStyles(): void {
    document.querySelectorAll<HTMLElement>(`.${CLASS_NAMES.FRAUD_MESSAGE}`).forEach((el) => {
      el.classList.remove(CLASS_NAMES.FRAUD_MESSAGE);
    });
    document.querySelectorAll<HTMLElement>(`.${CLASS_NAMES.FRAUD_MESSAGE_SHOW}`).forEach((el) => {
      el.classList.remove(CLASS_NAMES.FRAUD_MESSAGE_SHOW);
    });
  }

  markAsInvalidMessage(
    message: SerializedMessage,
    maxFileSizeBytes: number,
    i18n: Translations | undefined,
    selectors: WhatsappSelectorsType | undefined
  ): SerializedMessage | undefined {
    if (!selectors) {
      return;
    }
    const messageElement = document.querySelector<HTMLDivElement>(this.getMessageElementSelector(message.id, selectors));
    const element = messageElement?.querySelector<HTMLDivElement>(selectors.css.messageContainer);
    if (!element || !messageElement) {
      return message;
    }
    const reason =
      i18n?.["invalid_message.size"]({
        maxSize: formatBytes(maxFileSizeBytes),
      }) ?? "";
    DOMInjector.injectToDOM({ host: element, Component: MessageSyncInvalid, props: { reason }, namespace: CHAT_SYNCED_NAMESPACE });
  }

  cleanupMessagesDOMInstrumentations(cleanup = { selected: true, synced: true }, selectors: WhatsappSelectorsType | undefined) {
    const elements = selectors ? document.querySelectorAll<HTMLDivElement>(`[${selectors.domListener.threadElementSelector}]`) : [];
    Array.from(elements).forEach((element) => {
      cleanup.synced && element.classList.remove(CLASS_NAMES.SYNCED_THREAD);
      cleanup.selected && element.classList.remove(CLASS_NAMES.SELECTED_MESSAGE);
      cleanup.selected && element.classList.remove(CLASS_NAMES.MESSAGE_CONTAINER);
    });
    if (cleanup.synced) {
      DOMInjector.detachByNamespace(CHAT_SYNCED_NAMESPACE);
    }
    if (cleanup.selected) {
      DOMInjector.detachByNamespace(CHAT_CHECKBOXES_NAMESPACE);
    }
  }

  mountSelectDayCheckbox(store: SelectiveEntireDaySyncStore, selectors: WhatsappSelectorsType | undefined) {
    if (store.mounted || !selectors) {
      return;
    }

    const element = [...((document.querySelector<HTMLElement>(selectors.css.messagesContainer)?.children as unknown as HTMLElement[]) ?? [])].find(
      (e) => e.innerText.toUpperCase() === store.title
    );
    if (!element) {
      return store;
    }
    store.mounted = true;
    element?.classList.add(CLASS_NAMES.ENTIRE_DAY);
    DOMInjector.injectToDOM({ host: element, Component: EntireDayCheckbox, namespace: CHAT_CHECKBOXES_NAMESPACE, props: { store } });
  }

  addSelectMessageChcekbox(store: SelectiveSyncStore, selectors: WhatsappSelectorsType | undefined) {
    if (!selectors) {
      return;
    }

    const messageElement = document.querySelector<HTMLDivElement>(this.getMessageElementSelector(store.message.id, selectors));
    if (!messageElement) {
      return { store, pending: true };
    }
    messageElement?.classList.add(CLASS_NAMES.MESSAGE_CONTAINER);
    DOMInjector.injectToDOM({ host: messageElement, Component: MessageCheckbox, namespace: CHAT_CHECKBOXES_NAMESPACE, props: { store } });
    return { store, pending: false };
  }

  toggleMessageSelection(
    store: SelectiveSyncStore,
    value: boolean,
    selectedMessages: Set<SelectiveSyncStore> | undefined,
    selectors: WhatsappSelectorsType | undefined
  ) {
    const messageElement = document.querySelector<HTMLDivElement>(this.getMessageElementSelector(store.message.id ?? "", selectors!));
    if (value) {
      messageElement?.classList.add(CLASS_NAMES.SELECTED_MESSAGE);
    } else {
      messageElement?.classList.remove(CLASS_NAMES.SELECTED_MESSAGE);
    }

    widgetClient.send(WidgetCommands.MESSAGES_SELECTED, {
      messages:
        sortBy(Array.from(selectedMessages?.values() ?? []), "t").map(({ message }) => ({
          messageIsSent: Boolean(message.isSentByMe),
          messageText: message.text ?? "",
          messagePre:
            document
              .querySelector<HTMLDivElement>(this.getMessageElementSelector(message.id ?? "", selectors!))
              ?.querySelector<HTMLSpanElement>(`[${selectors?.css.prePlainTextAttribute}]`)
              ?.getAttribute(selectors?.css.prePlainTextAttribute ?? "")
              ?.trim() ?? "",
          messageTimeStamp: message.timestamp,
        })) ?? [],
    });
  }
}
